import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  FormLabel,
  Input,
  VStack,
  Text,
  Spinner,
  IconButton,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { ChevronDown, X } from 'lucide-react'
import { apiClient } from '@/lib/api/client'

const SEARCH_DEBOUNCE_MS = 1000

export interface SearchableSelectOption {
  id: number | string
  name: string
}

function normalizeApiOptions(
  raw: unknown,
  optionIdKey?: string
): SearchableSelectOption[] {
  const arr = Array.isArray(raw) ? raw : (raw as { data?: unknown[] })?.data
  if (!Array.isArray(arr)) return []
  return arr.map((item: Record<string, unknown>) => {
    const idFromKey = optionIdKey && item[optionIdKey] != null
      ? item[optionIdKey]
      : undefined
    return {
      id: (idFromKey ?? item.id ?? item.value ?? item.plate_number ?? item) as number | string,
      name: String(
        item.name ??
          item.label ??
          item.full_name ??
          item.plate_number ??
          item.total_expenses ??
          item.rate ??
          item.id ??
          item.value ??
          ''
      ),
    }
  })
}

export interface SearchableSelectProps {
  options: SearchableSelectOption[]
  /**
   * When provided, typing in the search box will fetch options from this URL (with debounce).
   * When search is empty, the passed `options` are shown. Use for server-side search.
   */
  optionsUrl?: string | null
  /** Query params merged into the request when calling optionsUrl (e.g. { is_active: 1 }). */
  optionsParams?: Record<string, string | number | boolean>
  /** Query param name for the search string. Default 'search'. */
  searchParamName?: string
  /** When set, option with this id is excluded from the list (e.g. to avoid same from/to). */
  excludeOptionId?: number | string | null
  /** When set, use this key from API items as option id (e.g. "plate_number" for trucks). */
  optionIdKey?: string
  value?: number | string | null
  onChange?: (value: number | string | null) => void
  placeholder?: string
  label?: string
  isInvalid?: boolean
  isDisabled?: boolean
  isLoading?: boolean
}

export function SearchableSelect({
  options,
  optionsUrl,
  optionsParams,
  searchParamName = 'search',
  excludeOptionId,
  optionIdKey,
  value,
  onChange,
  placeholder = 'Select option',
  label,
  isInvalid = false,
  isDisabled = false,
  isLoading = false,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchableSelectOption[]>([])
  const [searchError, setSearchError] = useState<string | null>(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  /** Persist option selected from API search so we can show its label after dropdown closes (when it's not in initial options). */
  const lastSelectedFromSearchRef = useRef<SearchableSelectOption | null>(null)

  const colors = useThemeColors()
  const bgColor = colors.cardBg
  const borderColor = colors.border
  const hoverBg = colors.sectionBg

  const useApiSearch = Boolean(optionsUrl?.trim())
  const isSearching = useApiSearch && searchQuery.trim().length > 0

  const excludeId = excludeOptionId != null ? String(excludeOptionId) : null
  const filterExcluded = <T extends { id: number | string }>(list: T[]) =>
    excludeId ? list.filter((opt) => String(opt.id) !== excludeId) : list

  // When using API search: show search results when user has typed, otherwise show passed options.
  // When not using API: filter options locally by searchQuery.
  const rawEffectiveOptions = useApiSearch
    ? (isSearching ? searchResults : options)
    : options.filter((opt) =>
        opt.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
  const effectiveOptions = filterExcluded(rawEffectiveOptions)

  const selectedOption =
    rawEffectiveOptions.find((opt) => String(opt.id) === String(value)) ||
    options.find((opt) => String(opt.id) === String(value)) ||
    (value != null && lastSelectedFromSearchRef.current && String(lastSelectedFromSearchRef.current.id) === String(value)
      ? lastSelectedFromSearchRef.current
      : null)

  const fetchSearch = useCallback(
    async (query: string) => {
      const url = optionsUrl?.trim()
      if (!url || !query.trim()) return
      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()
      setSearchLoading(true)
      setSearchError(null)
      try {
        const params: Record<string, string | number | boolean> = {
          ...optionsParams,
          [searchParamName]: query.trim(),
        }
        const { data } = await apiClient.get(url, {
          params,
          signal: abortControllerRef.current.signal,
        })
        setSearchResults(normalizeApiOptions(data, optionIdKey))
      } catch (err: unknown) {
        if ((err as { name?: string })?.name === 'CanceledError' || (err as { code?: string })?.code === 'ERR_CANCELED') {
          return
        }
        setSearchError(err instanceof Error ? err.message : 'Failed to load options')
        setSearchResults([])
      } finally {
        setSearchLoading(false)
      }
    },
    [optionsUrl, optionsParams, searchParamName, optionIdKey]
  )

  // Debounced API search when user types and optionsUrl is set
  useEffect(() => {
    if (!useApiSearch) return
    const query = searchQuery.trim()
    if (!query) {
      setSearchResults([])
      setSearchError(null)
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      fetchSearch(query)
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
    }
  }, [useApiSearch, searchQuery, fetchSearch])

  // Reset search state when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setSearchResults([])
      setSearchError(null)
      abortControllerRef.current?.abort()
    }
  }, [isOpen])

  // Clear persisted selection when value no longer matches (e.g. form reset or parent changed value)
  useEffect(() => {
    if (value == null || (lastSelectedFromSearchRef.current && String(lastSelectedFromSearchRef.current.id) !== String(value))) {
      lastSelectedFromSearchRef.current = null
    }
  }, [value])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      setTimeout(() => searchInputRef.current?.focus(), 0)
    }

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleSelectOption = (option: SearchableSelectOption) => {
    if (useApiSearch) {
      lastSelectedFromSearchRef.current = option
    }
    onChange?.(option.id)
    setIsOpen(false)
    setSearchQuery('')
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    lastSelectedFromSearchRef.current = null
    onChange?.(null)
    setSearchQuery('')
  }

  const handleToggle = () => {
    if (!isDisabled) setIsOpen(!isOpen)
  }

  const showLoading = isLoading || (useApiSearch && isSearching && searchLoading)
  const showError = useApiSearch && isSearching && searchError
  const showEmpty = effectiveOptions.length === 0 && !showLoading && !showError

  return (
    <Box position="relative" ref={dropdownRef}>
      {label && (
        <FormLabel fontWeight="medium" mb={2}>
          {label}
        </FormLabel>
      )}
      <Box
        position="relative"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        onClick={handleToggle}
      >
        <Input
          readOnly
          value={selectedOption?.name || ''}
          placeholder={placeholder}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          pr="2.5rem"
          cursor="pointer"
          _placeholder={{ fontSize: 'sm' }}
        />
        <Box
          position="absolute"
          right="0.5rem"
          top="50%"
          transform="translateY(-50%)"
          display="flex"
          alignItems="center"
          gap={1}
        >
          {selectedOption && !isDisabled && (
            <IconButton
              aria-label="Clear selection"
              icon={<X size={16} />}
              size="xs"
              variant="ghost"
              onClick={handleClear}
              _hover={{ bg: 'transparent' }}
            />
          )}
          {showLoading ? (
            <Spinner size="sm" />
          ) : (
            <ChevronDown
              size={16}
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          )}
        </Box>
      </Box>

      {isOpen && !isDisabled && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg={bgColor}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          boxShadow="lg"
          zIndex={1000}
          maxH="300px"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Box p={2} borderBottomWidth="1px" borderColor={borderColor}>
            <Input
              ref={searchInputRef}
              size="sm"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>

          <Box overflowY="auto" flex={1}>
            {showLoading ? (
              <VStack py={4}>
                <Spinner size="sm" />
                <Text fontSize="sm" color="gray.500">
                  Loading...
                </Text>
              </VStack>
            ) : showError ? (
              <Box p={4} textAlign="center">
                <Text fontSize="sm" color="red.500">
                  {searchError}
                </Text>
              </Box>
            ) : showEmpty ? (
              <Box p={4} textAlign="center">
                <Text fontSize="sm" color="gray.500">
                  {useApiSearch && isSearching ? 'No record found' : 'No options found'}
                </Text>
              </Box>
            ) : (
              <VStack spacing={0} align="stretch" py={1}>
                {effectiveOptions.map((option) => {
                  const isSelected = selectedOption?.id === option.id
                  return (
                    <Box
                      key={option.id}
                      px={4}
                      py={2}
                      cursor="pointer"
                      bg={isSelected ? 'blue.50' : 'transparent'}
                      _hover={{ bg: isSelected ? 'blue.50' : hoverBg }}
                      onClick={() => handleSelectOption(option)}
                    >
                      <Text fontSize="sm">{option.name}</Text>
                    </Box>
                  )
                })}
              </VStack>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
